import * as React from 'react'

import { BottleneckIdentifications, Trace } from '../domain/Trace'
import Loading from '../helper/loading'
import { encodeXhprofCallgraphNodeId, fullXhprofCallgraph, XhprofNode } from '../xhprof/XhprofCallgraph'
import { xhprofCompactGraph } from '../xhprof/XhprofCompactGraph'
import { computeXhprofStatsData, stackTraceXhprofParents } from '../xhprof/XhprofProfiler'
import { CallsView, createCallsViewCriteria } from './callgraph/calls-view'
import { Chart } from './callgraph/chart'
import { SelectedCall } from './callgraph/selected-call'
import { DescendantConfiguration, DescendantPanelGroup } from './callgraph/components/descendantPanelGroup'
import { BottleneckMessage } from '../domain/BottleneckMessage'

interface Props {
    router: any
    url: string
    params: { [key: string]: string }
    clickRoute: string
    trace: Trace
    showPlusWhenPositive: boolean
    startTracepointEvent: { startTime: string; duration: string } | null
    includesDynamicallyInstrumentedFunctions: boolean
    bottleneckIdentifications?: BottleneckIdentifications
    bottlenecks: BottleneckMessage[]
    bottleneckRoute: string
}

interface State {
    chartDimensions?: { width: number; height: number }
    searchString?: string
    parents: boolean
    children: boolean
    slowestPath: boolean
    selectedCallHistory: { [call: string]: true }
    screenDimensions: { width: number; height: number }
    callsViewCollapsed: boolean
    descendantPanelMaximized: boolean
}

const descendantPanels: DescendantConfiguration[] = [
    {
        active: toggleState => toggleState.parents,
        key: 'parents',
        label: 'Parents',
        data: (stats, props, call) => stats.parents,
    },
    {
        active: toggleState => toggleState.children,
        key: 'children',
        label: 'Children',
        data: (stats, props, call) => stats.children,
    },
    {
        active: toggleState => toggleState.slowestPath,
        key: 'slowestPath',
        label: 'Slowest Code Path',
        data: (stats, xhprofReport, call) => {
            if (!xhprofReport) {
                throw new Error('invalid data')
            }
            return stackTraceXhprofParents(xhprofReport.data, call)
        },
    },
]

type Panel = 'parents' | 'children' | 'slowestPath'

export class Callgraph extends React.Component<Props, State> {
    public static defaultProps = {
        clickRoute: 'XhprofProfilerBundle.Profile.callgraph',
        bottleneckRoute: 'XhprofProfilerBundle.Profile.bottlenecksReport',
    }

    private static PanelConfigurationKey = 'callgraph:panelPreference'

    public state: State = {
        parents: true,
        children: false,
        slowestPath: false,
        selectedCallHistory: {},
        screenDimensions: { width: 0, height: 0 },
        callsViewCollapsed: false,
        descendantPanelMaximized: false,
    }

    private chartElement

    public componentDidMount(): void {
        const screenDimensions = this.screenDimensions()
        const panelConfiguration = window.localStorage.getItem(Callgraph.PanelConfigurationKey)

        const selectedCall = this.props.params.fn

        if (selectedCall) {
            this.setState({ selectedCallHistory: { [selectedCall]: true } })
        }

        window.addEventListener('resize', this.onResize)

        if (panelConfiguration) {
            this.setState({ ...JSON.parse(panelConfiguration), screenDimensions })

            return
        }

        this.setState({ screenDimensions })
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.onResize)
    }

    public componentDidUpdate(prevProps: Props, prevState: State): void {
        window.scrollTo(0, 0)

        const changes = {
            parents: prevState.parents !== this.state.parents,
            children: prevState.children !== this.state.children,
            slowestPath: prevState.slowestPath !== this.state.slowestPath,
        }

        if (changes.parents || changes.children || changes.slowestPath) {
            window.localStorage.setItem(
                Callgraph.PanelConfigurationKey,
                JSON.stringify({
                    parents: this.state.parents,
                    children: this.state.children,
                    slowestPath: this.state.slowestPath,
                }),
            )
        }
    }

    public render() {
        const { trace, params, showPlusWhenPositive, startTracepointEvent, router, clickRoute, bottlenecks, bottleneckRoute } = this.props
        const { selectedCallHistory, screenDimensions, callsViewCollapsed } = this.state

        const maxStartCount = parseInt(params.maxStartCount || '3', 10)

        const criteria = createCallsViewCriteria(params, this.state.searchString)

        const topMargin = showPlusWhenPositive ? 231 : 151

        if (!trace || !trace.xhprofReportCompact || !trace.xhprofReport || !params || !trace.rawData) {
            return <Loading />
        }

        let { xhprofReportCompact } = trace

        // all calls selected during components lifecycle should be visible in tree
        // therefore pass history of selected call to xhprofCompactGraph
        if (Object.keys(selectedCallHistory).length > 0) {
            const missingNodes = Object.keys(selectedCallHistory).filter(call => !(call in xhprofReportCompact.data))

            if (missingNodes.length > 0) {
                xhprofReportCompact = {
                    ...xhprofReportCompact,
                    data: xhprofCompactGraph(trace.xhprofReport.data, 90, selectedCallHistory),
                }
            }
        }

        const { calls, ...treeData } = fullXhprofCallgraph(
            xhprofReportCompact.data,
            criteria.compareMode,
            Object.keys(selectedCallHistory),
            criteria.showMemory,
            maxStartCount,
        )

        let selectedCall: string | undefined = params.fn

        // prevent crash when an unknown call is given via URL
        if (selectedCall && !xhprofReportCompact.data[selectedCall]) {
            selectedCall = undefined
        }

        if (!selectedCall && calls && Object.keys(calls).length) {
            selectedCall = Object.keys(calls)[0]
        }

        const isRootCall = selectedCall === 'main()'
        const maxWt = treeData.total

        const callInfo = selectedCall ? xhprofReportCompact.data[selectedCall] || null : null

        const stats =
            selectedCall && trace.xhprofReport
                ? computeXhprofStatsData(trace.rawData, trace.xhprofReport, selectedCall, criteria.compareMode)
                : null

        const hideCallgraphThreshold = callsViewCollapsed ? 690 : 1190

        const hideCallgraph = screenDimensions.width < hideCallgraphThreshold

        const globalWarningBoxHeight = jQuery('.global-warning-box').height() || 0
        const extraStyles = { top: topMargin + globalWarningBoxHeight }

        return (
            <div
                className={'callgraph callgraph-full-width' + (callsViewCollapsed ? ' callsview-collapsed' : '')}
                ref={this.updateDimensions}
                style={extraStyles}
            >
                {!hideCallgraph && treeData && this.state.chartDimensions ? (
                    <Chart
                        tree={treeData}
                        totalWalltime={maxWt}
                        selectedNode={encodeXhprofCallgraphNodeId(selectedCall || '')}
                        rootNode={selectedCall || ''}
                        dimensions={this.state.chartDimensions}
                        onNodeSelected={(call: XhprofNode) => this.selectFunction(call.fqcn)}
                        unfoldCalls={(foldedCalls: string[]) => this.unfold(foldedCalls)}
                        backToMain={this.backToMain}
                        showAsDeltas={showPlusWhenPositive}
                        centerOffset={512}
                        marginLeft={callsViewCollapsed ? 0 : 660}
                        memory={criteria.showMemory}
                        maxStartCount={maxStartCount}
                        onChangeMaxStartCount={maxStartCount => this.setQueryParameter({ maxStartCount })}
                    />
                ) : null}
                <div className="overlay first">
                    <CallsView
                        startTracepointEvent={startTracepointEvent}
                        trace={trace}
                        criteria={criteria}
                        selectFunction={this.selectFunction}
                        router={router}
                        clickRoute={clickRoute}
                        changeSearchString={this.changeSearchString}
                        commitSearchString={this.commitSearchString}
                        params={this.props.params}
                        collapsable
                        onCollapseChange={collapsed => this.setState({ callsViewCollapsed: collapsed })}
                    />
                </div>
                <div className="overlay second">
                    {selectedCall && stats ? (
                        <div className="call-info">
                            <SelectedCall
                                callInfo={callInfo || undefined}
                                statsData={stats}
                                call={selectedCall}
                                selectMode="callgraph-new"
                                onSelectNode={this.selectFunction}
                                compareMode={showPlusWhenPositive}
                                includesDynamicallyInstrumentedFunctions={this.props.includesDynamicallyInstrumentedFunctions}
                                stackTrace={
                                    stats ? Object.keys(descendantPanels[2].data(stats, this.props.trace.xhprofReport, selectedCall)) : null
                                }
                                params={this.props.params}
                                bottleneckIdentifications={this.props.bottleneckIdentifications}
                                bottlenecks={bottlenecks}
                                bottleneckRoute={bottleneckRoute}
                                isRootCall={isRootCall}
                            >
                                <DescendantPanelGroup
                                    panelsConfig={descendantPanels}
                                    toggledPanels={{
                                        parents: this.state.parents,
                                        children: this.state.children,
                                        slowestPath: this.state.slowestPath,
                                    }}
                                    onTogglePanel={this.togglePanel}
                                    onToggleMaximizePanel={this.toggleMaximizePanel}
                                    onSelectFunction={this.selectFunction}
                                    selectedCall={selectedCall}
                                    stats={stats}
                                    xhprofReport={this.props.trace.xhprofReport}
                                    descendantPanelMaximized={this.state.descendantPanelMaximized}
                                    showPlusWhenPositive={showPlusWhenPositive}
                                />
                            </SelectedCall>
                        </div>
                    ) : null}
                </div>
            </div>
        )
    }

    private onResize = () => {
        this.setState({ screenDimensions: this.screenDimensions() })

        if (!this.chartElement) {
            return
        }
        const $wrap = jQuery(this.chartElement)

        this.setState({ chartDimensions: { width: $wrap.width(), height: $wrap.height() } })
    }

    private screenDimensions = () => ({ width: document.body.clientWidth, height: document.body.clientHeight })

    private updateDimensions = element => {
        this.chartElement = element

        if (!element) {
            return
        }
        const $wrap = jQuery(element)

        this.setState({ chartDimensions: { width: $wrap.width(), height: $wrap.height() } })
    }

    private selectFunction = call => {
        this.setState({ selectedCallHistory: { ...this.state.selectedCallHistory, [call]: true } })
        this.setQueryParameter({ fn: call })
    }

    private unfold = (calls: string[]) => {
        let callsMap = {}
        calls.forEach(call => {
            callsMap = { ...callsMap, [call]: true }
        })
        this.setState({ selectedCallHistory: { ...this.state.selectedCallHistory, ...callsMap } })
    }

    private backToMain = () => {
        this.setQueryParameter({ fn: 'main()' })
    }

    private setQueryParameter = values => {
        this.props.router.goTo(this.props.clickRoute, { ...this.props.params, ...values })
    }

    private togglePanel = (panel: Panel) => {
        switch (panel) {
            case 'parents':
                this.setState({ parents: !this.state.parents, descendantPanelMaximized: false })
                return
            case 'children':
                this.setState({ children: !this.state.children, descendantPanelMaximized: false })
                return
            case 'slowestPath':
                this.setState({ slowestPath: !this.state.slowestPath, descendantPanelMaximized: false })
                return
        }
    }

    private toggleMaximizePanel = (panel: Panel) => {
        switch (panel) {
            case 'parents':
                this.setState({
                    parents: true,
                    children: false,
                    slowestPath: false,
                    descendantPanelMaximized: !this.state.descendantPanelMaximized,
                })
                return
            case 'children':
                this.setState({
                    children: true,
                    parents: false,
                    slowestPath: false,
                    descendantPanelMaximized: !this.state.descendantPanelMaximized,
                })
                return
            case 'slowestPath':
                this.setState({
                    slowestPath: true,
                    parents: false,
                    children: false,
                    descendantPanelMaximized: !this.state.descendantPanelMaximized,
                })
                return
        }
    }

    private changeSearchString = (searchString: string) => {
        this.setState({ searchString })
    }

    private commitSearchString = ev => {
        ev.preventDefault()
        this.setQueryParameter({ query: this.state.searchString, page: 1 })
    }
}
